import React, { useState, memo } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { ChevronLeftIcon, ChevronRightIcon, EyeIcon, LinkIcon, ArrowUpDownIcon, SearchIcon, CheckCircleIcon, AlertCircleIcon } from 'lucide-react';
export function PostaInArrivoTab() {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAssociationModal, setShowAssociationModal] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  // Dati di esempio per la demo
  const emailRicevute = [{
    id: 1,
    oggetto: 'Re: [CF-2023-0042] Promemoria pagamento fattura #12345',
    mittente: 'mario.rossi@example.com',
    dataRicezione: '2023-06-15T16:45:00',
    pratica: 'CF-2023-0042',
    cliente: 'Rossi SRL',
    associata: true,
    letta: true,
    tipoSollecito: 'amichevole',
    estratto: 'Buongiorno, in merito alla vostra comunicazione vi informiamo che il pagamento è stato effettuato in data odierna...'
  }, {
    id: 2,
    oggetto: 'Re: [CF-2023-0036] Sollecito pagamento fattura #10982',
    mittente: 'amministrazione@verdi.it',
    dataRicezione: '2023-06-14T14:30:00',
    pratica: 'CF-2023-0036',
    cliente: 'Verdi SpA',
    associata: true,
    letta: true,
    tipoSollecito: 'formale',
    estratto: 'Gentili signori, abbiamo riscontrato un problema con il pagamento. Provvederemo a saldare entro la prossima settimana...'
  }, {
    id: 3,
    oggetto: 'Richiesta chiarimenti su fattura',
    mittente: 'info@bianchi.com',
    dataRicezione: '2023-06-13T11:20:00',
    pratica: null,
    cliente: 'Bianchi & Co.',
    associata: false,
    letta: false,
    tipoSollecito: null,
    estratto: 'Vorrei avere chiarimenti in merito alla fattura n. 9876 del 15/05/2023. Non mi risulta di aver ricevuto la merce...'
  }, {
    id: 4,
    oggetto: 'Re: [CF-2023-0018] Diffida ad adempiere - Fattura #8765',
    mittente: 'azienda@pec.neri.it',
    dataRicezione: '2023-06-11T09:15:00',
    pratica: 'CF-2023-0018',
    cliente: 'Neri Costruzioni',
    associata: true,
    letta: true,
    tipoSollecito: 'pec',
    estratto: "In risposta alla vostra PEC, vi comunichiamo che procederemo al pagamento immediato dell'importo dovuto..."
  }, {
    id: 5,
    oggetto: 'Informazioni su pagamento',
    mittente: 'contabilita@gialli.org',
    dataRicezione: '2023-06-09T10:45:00',
    pratica: null,
    cliente: 'Gialli Associati',
    associata: false,
    letta: false,
    tipoSollecito: null,
    estratto: 'Buongiorno, vi scrivo per avere informazioni sui pagamenti in sospeso della nostra azienda...'
  }];
  // Funzione per ottenere l'etichetta del tipo di sollecito
  const getTipoSollecitoLabel = tipo => {
    if (!tipo) return null;
    switch (tipo) {
      case 'amichevole':
        return 'Sollecito Amichevole';
      case 'formale':
        return 'Sollecito Formale';
      case 'duro':
        return 'Sollecito Duro';
      case 'pec':
        return 'PEC Ufficiale';
      default:
        return tipo;
    }
  };
  // Funzione per formattare la data
  const formatDate = dateString => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };
  // Funzione per estrarre il codice pratica dall'oggetto
  const extractPraticaCode = oggetto => {
    const match = oggetto.match(/\[(CF-\d{4}-\d{4})\]/);
    return match ? match[1] : null;
  };
  // Funzione per evidenziare il codice pratica nell'oggetto
  const highlightPraticaCode = oggetto => {
    const code = extractPraticaCode(oggetto);
    if (code) {
      return oggetto.replace(`[${code}]`, `<span class="font-medium text-[#1A3E5C]">[${code}]</span>`);
    }
    return oggetto;
  };
  // Gestione associazione email a pratica
  const handleAssociateEmail = email => {
    setSelectedEmail(email);
    setShowAssociationModal(true);
  };
  // Filtro per la ricerca
  const filteredEmails = emailRicevute.filter(email => email.oggetto.toLowerCase().includes(searchQuery.toLowerCase()) || email.mittente.toLowerCase().includes(searchQuery.toLowerCase()) || email.pratica && email.pratica.toLowerCase().includes(searchQuery.toLowerCase()) || email.cliente.toLowerCase().includes(searchQuery.toLowerCase()));
  return <>
      <Card data-id="element-319">
        <div className="sm:flex sm:items-center" data-id="element-320">
          <div className="sm:flex-auto" data-id="element-321">
            <h3 className="text-lg font-medium text-gray-900" data-id="element-322">
              Posta in arrivo
            </h3>
            <p className="mt-2 text-sm text-gray-500" data-id="element-323">
              Elenco di tutte le comunicazioni ricevute dai clienti, ordinate
              dalla più recente.
            </p>
          </div>
        </div>
        <div className="mt-4 flex justify-between items-center" data-id="element-324">
          <div className="w-full max-w-lg" data-id="element-325">
            <div className="relative mt-1 rounded-md shadow-sm" data-id="element-326">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3" data-id="element-327">
                <SearchIcon className="h-5 w-5 text-gray-400" aria-hidden="true" data-id="element-328" />
              </div>
              <input type="text" className="block w-full rounded-md border-gray-300 pl-10 focus:border-[#1A3E5C] focus:ring-[#1A3E5C] sm:text-sm" placeholder="Cerca per oggetto, mittente, pratica o cliente..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-329" />
            </div>
          </div>
          <div className="ml-2" data-id="element-330">
            <Button variant="secondary" icon={<ArrowUpDownIcon className="h-4 w-4 mr-2" data-id="element-332" />} data-id="element-331">
              Ordina
            </Button>
          </div>
        </div>
        <div className="mt-6 overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg" data-id="element-333">
          <table className="min-w-full divide-y divide-gray-300" data-id="element-334">
            <thead className="bg-gray-50" data-id="element-335">
              <tr data-id="element-336">
                <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sm:pl-6" data-id="element-337">
                  Stato
                </th>
                <th scope="col" className="px-3 py-3.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-338">
                  Oggetto
                </th>
                <th scope="col" className="px-3 py-3.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-339">
                  Mittente
                </th>
                <th scope="col" className="px-3 py-3.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-340">
                  Data ricezione
                </th>
                <th scope="col" className="px-3 py-3.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-341">
                  Cliente
                </th>
                <th scope="col" className="px-3 py-3.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-342">
                  Pratica
                </th>
                <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6" data-id="element-343">
                  <span className="sr-only" data-id="element-344">Azioni</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white" data-id="element-345">
              {filteredEmails.map(email => <tr key={email.id} className={email.letta ? '' : 'bg-[#1A3E5C]/5'} data-id="element-346">
                  <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6" data-id="element-347">
                    {email.letta ? <CheckCircleIcon className="h-5 w-5 text-gray-400" data-id="element-348" /> : <AlertCircleIcon className="h-5 w-5 text-[#1A3E5C]" data-id="element-349" />}
                  </td>
                  <td className="px-3 py-4 text-sm text-gray-900" data-id="element-350">
                    <div className="font-medium" dangerouslySetInnerHTML={{
                  __html: highlightPraticaCode(email.oggetto)
                }} data-id="element-351" />
                    <div className="text-gray-500 truncate max-w-md" data-id="element-352">
                      {email.estratto}
                    </div>
                    {email.tipoSollecito && <div className="mt-1" data-id="element-353">
                        <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800" data-id="element-354">
                          {getTipoSollecitoLabel(email.tipoSollecito)}
                        </span>
                      </div>}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500" data-id="element-355">
                    {email.mittente}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500" data-id="element-356">
                    {formatDate(email.dataRicezione)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500" data-id="element-357">
                    {email.cliente}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500" data-id="element-358">
                    {email.associata ? <span className="inline-flex items-center rounded-full bg-[#4A8F6A]/20 px-2.5 py-0.5 text-xs font-medium text-[#4A8F6A]" data-id="element-359">
                        {email.pratica}
                      </span> : <button type="button" onClick={() => handleAssociateEmail(email)} className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800 hover:bg-gray-200" data-id="element-360">
                        <LinkIcon className="h-3 w-3 mr-1" data-id="element-361" />
                        Associa
                      </button>}
                  </td>
                  <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6" data-id="element-362">
                    <button type="button" className="text-[#1A3E5C] hover:text-[#15324a]" data-id="element-363">
                      <EyeIcon className="h-5 w-5" data-id="element-364" />
                      <span className="sr-only" data-id="element-365">Visualizza</span>
                    </button>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 mt-4" data-id="element-366">
          <div className="flex flex-1 justify-between sm:hidden" data-id="element-367">
            <button onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-368">
              Precedente
            </button>
            <button onClick={() => setCurrentPage(currentPage + 1)} className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-369">
              Successiva
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between" data-id="element-370">
            <div data-id="element-371">
              <p className="text-sm text-gray-700" data-id="element-372">
                Visualizzati{' '}
                <span className="font-medium" data-id="element-373">{filteredEmails.length}</span> di{' '}
                <span className="font-medium" data-id="element-374">{emailRicevute.length}</span>{' '}
                risultati
              </p>
            </div>
            <div data-id="element-375">
              <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination" data-id="element-376">
                <button onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0" data-id="element-377">
                  <span className="sr-only" data-id="element-378">Precedente</span>
                  <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-379" />
                </button>
                <span aria-current="page" className="relative z-10 inline-flex items-center bg-[#1A3E5C] px-4 py-2 text-sm font-semibold text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1A3E5C]" data-id="element-380">
                  {currentPage}
                </span>
                <button onClick={() => setCurrentPage(currentPage + 1)} className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0" data-id="element-381">
                  <span className="sr-only" data-id="element-382">Successiva</span>
                  <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-383" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      </Card>
      {/* Modal per associare email a pratica */}
      {showAssociationModal && selectedEmail && <div className="fixed inset-0 z-10 overflow-y-auto" data-id="element-384">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-385">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true" data-id="element-386">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-387"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-388">
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6" data-id="element-389">
              <div data-id="element-390">
                <div className="mt-3 text-center sm:mt-5" data-id="element-391">
                  <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-392">
                    Associa email a pratica
                  </h3>
                  <div className="mt-2" data-id="element-393">
                    <p className="text-sm text-gray-500" data-id="element-394">
                      Seleziona la pratica a cui associare questa email:
                    </p>
                    <div className="mt-4" data-id="element-395">
                      <label htmlFor="pratica" className="block text-sm font-medium text-gray-700" data-id="element-396">
                        Codice pratica
                      </label>
                      <select id="pratica" name="pratica" className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-[#1A3E5C] focus:border-[#1A3E5C] sm:text-sm rounded-md" defaultValue="" data-id="element-397">
                        <option value="" disabled data-id="element-398">
                          Seleziona una pratica
                        </option>
                        <option value="CF-2023-0042" data-id="element-399">
                          CF-2023-0042 - Rossi SRL
                        </option>
                        <option value="CF-2023-0036" data-id="element-400">
                          CF-2023-0036 - Verdi SpA
                        </option>
                        <option value="CF-2023-0029" data-id="element-401">
                          CF-2023-0029 - Bianchi & Co.
                        </option>
                        <option value="CF-2023-0018" data-id="element-402">
                          CF-2023-0018 - Neri Costruzioni
                        </option>
                        <option value="CF-2023-0039" data-id="element-403">
                          CF-2023-0039 - Gialli Associati
                        </option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense" data-id="element-404">
                <button type="button" className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-[#1A3E5C] text-base font-medium text-white hover:bg-[#15324a] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1A3E5C] sm:col-start-2 sm:text-sm" onClick={() => setShowAssociationModal(false)} data-id="element-405">
                  Associa
                </button>
                <button type="button" className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1A3E5C] sm:mt-0 sm:col-start-1 sm:text-sm" onClick={() => setShowAssociationModal(false)} data-id="element-406">
                  Annulla
                </button>
              </div>
            </div>
          </div>
        </div>}
    </>;
}